import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  isCurrentUserSuperUser,
  isCurrentUserStaffOrSuperAdmin,
  isCurrentMemberOrgAdmin,
  isCurrentMemberTeamLeader,
  isCurrentMemberDirector,
  isCurrentMemberRegularUser,
  getMemberId,
} from '../common/selectors/auth';
import { UserContextInitializationState } from '../common/slices/userContextSlice';
import { PORTAL_ROUTES } from './routeConfig';
import useFeatures from '../common/hooks/useFeatures';

import DataAnalytics from 'areas/data-analytics/components/DataAnalytics';
import OrgOutcomes from 'areas/org-outcomes/components/Outcomes';
import Copilot from 'areas/copilot/components/Copilot';
import Lost from 'areas/404/components';
import OrganizationalGoals from '../areas/recommendations/components/OrganizationalGoals';
import TeamLeaderLeaderboard from '../areas/leaderboard/components/TeamLeaderLeaderboard';
import DashboardMain from '../areas/dashboard/components/DashboardMain';
import DashboardRedirect from '../areas/dashboard/components/DashboardRedirect';
import OrgSettings from '../areas/org-settings/components/OrgSettings';
import { PerformanceMetricsManager } from '../areas/settings/performance-metrics/components/PerformanceMetricsManager';

import TeamProjects from 'areas/team-recommendations/components/TeamProjects';
import TeamProject from 'areas/team-recommendations/components/TeamProject';
import TeamProjectAssignGoal from 'areas/team-recommendations/components/TeamProjectAssignGoal';

import FoundationProjects from 'areas/foundations/components/FoundationProjects';
import FoundationProject from 'areas/foundations/components/FoundationProject';

import CultureHub from 'areas/culture-hub/components/CultureHub';
import FocusAreaDetail from '@/areas/culture-hub/components/interventions/action-planner/FocusAreaDetail';
import FocusPlanRecommendations from '@/areas/culture-hub/components/interventions/action-planner/FocusPlanRecommendations';

import AssessmentRouter from 'areas/assessment-schedule/AssessmentRouter';
import PulseSurveyOverview from 'areas/pulse-surveys/components/PulseSurveyOverview';
import PulseSurveyDetails from 'areas/pulse-surveys/components/PulseSurveyDetails';
import PulseScheduleStructureEditorPage from 'areas/pulse-surveys/components/PulseScheduleStructureEditorPage';
import MemberManagement from '../areas/settings/employee-management/components/MemberManagement';
import TeamManagement from '@/areas/team-management/components/TeamManagement';
import TeamCreate from '@/areas/team-management/components/TeamCreate';
import ImportMemberReviewData from 'areas/settings/employee-management/components/ImportMemberReviewData';
import ImportTeamReviewData from '@/areas/team-management/components/ImportTeamReviewData';
import ImportMemberResult from 'areas/settings/employee-management/components/ImportMemberResult';
import MemberEdit from 'areas/settings/employee-management/components/MemberEdit';
import RosterReviewPage from 'areas/settings/employee-management/components/RosterReviewPage';
import GlossaryPage from 'areas/glossary/components/GlossaryPage';
import SummaryPage from 'areas/guided-experience/SummaryPage';
import KnowledgeBasePage from 'areas/knowledge-base/components/KnowledgeBasePage';
import ResourceListPage from 'areas/knowledge-base/components/ResourceListPage';
import LoadingSpinner from 'common/components/LoadingSpinner';

const AccessDenied = () => (
  <div style={{ padding: '40px', textAlign: 'center' }} data-testid="access-denied-page">
    <h1 style={{ fontSize: '48px', color: 'red' }}>ACCESS DENIED</h1>
    <p>You do not have permission to access this page.</p>
  </div>
);

function PortalRouter() {
  const isSuperUser = useSelector(isCurrentUserSuperUser);
  const isStaff = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isUserOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const isUserTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const isUserDirector = useSelector(isCurrentMemberDirector);
  const isUserRegularMember = useSelector(isCurrentMemberRegularUser);
  // Current context user's employee id (used to filter leader-scoped queries)
  const currentEmployeeId = useSelector(getMemberId);
  const userContextState = useSelector((state) => state.userContext.initializationState);
  const { isFeatureEnabled, initialized: featuresInitialized } = useFeatures();

  const needsOrgContext = !isStaff && !isSuperUser;
  const isUserContextLoaded = needsOrgContext
    ? userContextState === UserContextInitializationState.ORGANIZATION_INITIALIZED
    : userContextState === UserContextInitializationState.USER_INITIALIZED || userContextState === UserContextInitializationState.ORGANIZATION_INITIALIZED;

  if (!isUserContextLoaded) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
          <LoadingSpinner />
      </div>
    );
  }

  const hasAnyAccess = isStaff || isSuperUser || isUserOrgAdmin || isUserTeamLeader || isUserDirector || isUserRegularMember;

  if (!hasAnyAccess) {
    return <Navigate to="/app/access-denied" replace />;
  }

  const hasPermission = (allowedRoles, featureFlag) => {
    // Only deny access based on a feature flag if feature flags are fully initialized.
    // This prevents a brief "Access Denied" flash while feature flags are loading on first login.
    if (featureFlag && featuresInitialized && !isFeatureEnabled(featureFlag)) {
      return false;
    }

    // Superuser can access everything
    if (isSuperUser) return true;

    // Staff can access a route only if it explicitly allows 'staff'
    if (isStaff && allowedRoles.includes('staff')) return true;

    if (allowedRoles.includes('orgAdmin') && isUserOrgAdmin) return true;
    if (allowedRoles.includes('director') && isUserDirector) return true;
    if (allowedRoles.includes('teamLeader') && isUserTeamLeader) return true;
    if (allowedRoles.includes('regularUser') && isUserRegularMember) return true;

    return false;
  };

  const ProtectedElement = ({ element, allowedRoles, featureFlag, redirectTo }) =>
    hasPermission(allowedRoles, featureFlag)
      ? element
      : redirectTo
        ? <Navigate to={redirectTo} replace />
        : <AccessDenied />;

  return (
    <Routes>
      <Route path="access-denied" element={<AccessDenied />} />

      {/* Dashboard - team leaders are redirected before permission check */}
      <Route
        path={PORTAL_ROUTES.dashboard.path}
        element={
          isUserTeamLeader && !isStaff && !isSuperUser && !isUserOrgAdmin && !isUserDirector
            ? <DashboardRedirect />
            : <ProtectedElement
                allowedRoles={PORTAL_ROUTES.dashboard.allowedRoles}
                element={<DashboardMain />}
              />
        }
      />

      {/* Culture Hub */}
      <Route
        path={PORTAL_ROUTES.cultureHub.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.cultureHub.allowedRoles} featureFlag={PORTAL_ROUTES.cultureHub.featureFlag} redirectTo="/app/dashboard" element={<CultureHub leader_id={currentEmployeeId} />} />}
      />
      <Route
        path={`${PORTAL_ROUTES.cultureHub.path}/:tab`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.cultureHub.allowedRoles} featureFlag={PORTAL_ROUTES.cultureHub.featureFlag} redirectTo="/app/dashboard" element={<CultureHub leader_id={currentEmployeeId} />} />}
      />

      {/* Recommendation detail (single recommendation view) */}
      <Route
        path={`${PORTAL_ROUTES.cultureHubInterventions.path}/recommendation/:templateId/:taskIndex`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.cultureHubInterventions.allowedRoles} redirectTo="/app/dashboard" element={<FocusAreaDetail />} />}
      />
      <Route
        path={PORTAL_ROUTES.cultureHubFocusPlanRecommendations.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.cultureHubFocusPlanRecommendations.allowedRoles} redirectTo="/app/dashboard" element={<FocusPlanRecommendations />} />}
      />

      {/* Analytics */}
      <Route
        path={PORTAL_ROUTES.analytics.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.analytics.allowedRoles} element={<DataAnalytics />} />}
      />

      {/* Assessments */}
      <Route
        path={`${PORTAL_ROUTES.assessments.path}/*`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.assessments.allowedRoles} element={<AssessmentRouter />} />}
      />

      {/* Pulse Surveys */}
      <Route
        path={PORTAL_ROUTES.pulseSurveys.path}
        element={
          <ProtectedElement
            allowedRoles={PORTAL_ROUTES.pulseSurveys.allowedRoles}
            featureFlag={PORTAL_ROUTES.pulseSurveys.featureFlag}
            element={<PulseSurveyOverview />}
          />
        }
      />
      <Route
        path={`${PORTAL_ROUTES.pulseSurveys.path}/:id`}
        element={
          <ProtectedElement
            allowedRoles={PORTAL_ROUTES.pulseSurveys.allowedRoles}
            featureFlag={PORTAL_ROUTES.pulseSurveys.featureFlag}
            element={<PulseSurveyDetails />}
          />
        }
      />
      <Route
        path={`${PORTAL_ROUTES.pulseSurveys.path}/structures/new`}
        element={
          <ProtectedElement
            allowedRoles={PORTAL_ROUTES.pulseSurveys.allowedRoles}
            featureFlag={PORTAL_ROUTES.pulseSurveys.featureFlag}
            element={<PulseScheduleStructureEditorPage />}
          />
        }
      />
      <Route
        path={`${PORTAL_ROUTES.pulseSurveys.path}/structures/:structureId/edit`}
        element={
          <ProtectedElement
            allowedRoles={PORTAL_ROUTES.pulseSurveys.allowedRoles}
            featureFlag={PORTAL_ROUTES.pulseSurveys.featureFlag}
            element={<PulseScheduleStructureEditorPage />}
          />
        }
      />

      {/* Team Projects */}

      {/* TP: This feature is deprecated for the moment, until we figure out what to do with it */}

{/*       <Route
        path={PORTAL_ROUTES.teamProjects.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teamProjects.allowedRoles} element={<TeamProjects />} />}
      />
      <Route
        path={`${PORTAL_ROUTES.teamProjects.path}/edit/:id`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teamProjects.allowedRoles} element={<TeamProject />} />}
      />
      <Route
        path={`${PORTAL_ROUTES.teamProjects.path}/new/:id`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teamProjects.allowedRoles} element={<TeamProjectAssignGoal />} />}
      />
 */}
      {/* Foundations - NOT accessible to team leaders */}
      <Route
        path={PORTAL_ROUTES.foundations.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.foundations.allowedRoles} element={<FoundationProjects />} />}
      />
      <Route
        path={`${PORTAL_ROUTES.foundations.path}/edit/:id`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.foundations.allowedRoles} element={<FoundationProject />} />}
      />

      {/* Outcomes */}
      <Route
        path={PORTAL_ROUTES.outcomes.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.outcomes.allowedRoles} element={<OrgOutcomes />} />}
      />

      {/* Org Goals */}
      <Route
        path={PORTAL_ROUTES.orgGoals.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.orgGoals.allowedRoles} element={<OrganizationalGoals />} />}
      />
      <Route
        path={PORTAL_ROUTES.leaderboard.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.leaderboard.allowedRoles} element={<TeamLeaderLeaderboard />} />}
      />
      <Route
        path={`${PORTAL_ROUTES.orgGoals.path}/:id`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.orgGoals.allowedRoles} element={<OrganizationalGoals />} />}
      />

      {/* Copilot */}
      <Route
        path={PORTAL_ROUTES.copilot.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.copilot.allowedRoles} element={<Copilot />} />}
      />

      {/* Member Management */}
      <Route
        path={PORTAL_ROUTES.teamManagement.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teamManagement.allowedRoles} element={<MemberManagement />} />}
      />
      <Route
        path={`${PORTAL_ROUTES.teamManagement.path}/:memberId`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teamManagement.allowedRoles} element={<MemberEdit />} />}
      />
      <Route
        path={PORTAL_ROUTES.rosterReview.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.rosterReview.allowedRoles} element={<RosterReviewPage />} />}
      />

      {/* Teams Management */}
      <Route
        path={PORTAL_ROUTES.teams.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teams.allowedRoles} element={<TeamManagement />} />}
      />
      <Route
        path={`${PORTAL_ROUTES.teams.path}/create`}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teams.allowedRoles} element={<TeamCreate />} />}
      />

      {/* Knowledge Base */}
      <Route
        path={PORTAL_ROUTES.knowledgeBase.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.knowledgeBase.allowedRoles} element={<KnowledgeBasePage />} />}
      />
      <Route
        path={PORTAL_ROUTES.knowledgeBaseResources.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.knowledgeBaseResources.allowedRoles} element={<ResourceListPage />} />}
      />

      {/* Org Settings */}
      <Route
        path={PORTAL_ROUTES.orgSettings.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.orgSettings.allowedRoles} element={<OrgSettings />} />}
      />

      {/* Import Review */}
      <Route
        path={PORTAL_ROUTES.importReview.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.importReview.allowedRoles} element={<ImportMemberReviewData />} />}
      />
      <Route
        path={PORTAL_ROUTES.teamImportReview.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.teamImportReview.allowedRoles} element={<ImportTeamReviewData />} />}
      />
      <Route path={`${PORTAL_ROUTES.teamManagement.path}/import/result`} element={<ImportMemberResult />} />

      {/* Metrics (conditionally rendered if present in PORTAL_ROUTES) */}
      {PORTAL_ROUTES.metrics && PORTAL_ROUTES.metrics.path && (
        <Route
          path={PORTAL_ROUTES.metrics.path}
          element={<ProtectedElement allowedRoles={PORTAL_ROUTES.metrics.allowedRoles} element={<PerformanceMetricsManager />} />}
        />
      )}

      {/* Glossary */}
      <Route
        path={PORTAL_ROUTES.glossary.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.glossary.allowedRoles} featureFlag={PORTAL_ROUTES.glossary.featureFlag} element={<GlossaryPage />} />}
      />

      {/* Summary — opens the guided experience tour */}
      <Route
        path={PORTAL_ROUTES.summary.path}
        element={<ProtectedElement allowedRoles={PORTAL_ROUTES.summary.allowedRoles} featureFlag={PORTAL_ROUTES.summary.featureFlag} element={<SummaryPage />} />}
      />

      {/* Catch-all - 404 or Access Denied */}
      <Route path="*" element={<Lost />} />
    </Routes>
  );
}

export default PortalRouter;
